import { useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useBand } from '../context/BandContext';
import { api } from '../lib/api';
import { BandWorkspace, BandToolbar, BandTabs, BandList } from '../components/styled/bands';

function BandPage() {
  const { band, bandId, refreshBand } = useBand();
  const location = useLocation();
  const [params] = useSearchParams();
  const requestedTab = params.get('tab') || 'members';
  const tab = requestedTab === 'settings' ? 'settings' : 'members';
  const base = `/bands/${bandId}`;
  const [email, setEmail] = useState('');
  const [name, setName] = useState(band.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const perform = async (action, message) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
      setNotice(message);
      refreshBand();
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  };

  if (requestedTab === 'settings' && !band.canManage) {
    return <Navigate to="/logout" replace state={{
      from: location, authError: 'Only band managers can access this view.',
    }} />;
  }

  return (
    <BandWorkspace>
      <BandTabs aria-label="Band administration">
        <Link to={`${base}?tab=members`} aria-current={tab === 'members' ? 'page' : undefined}>
          Members
        </Link>
        {band.canManage ? (
          <Link to={`${base}?tab=settings`} aria-current={tab === 'settings' ? 'page' : undefined}>
            Settings
          </Link>
        ) : null}
      </BandTabs>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}

      {tab === 'members' ? (
        <>
          <h2>Members</h2>
          {band.canManage ? (
            <BandToolbar>
              <form onSubmit={(event) => {
                event.preventDefault();
                perform(async () => {
                  await api.addBandMember(bandId, email.trim());
                  setEmail('');
                }, 'Member added.');
              }}>
                <label>
                  Member email
                  <input type="email" required value={email} disabled={busy}
                    onChange={(event) => setEmail(event.target.value)} />
                </label>
                <button type="submit" disabled={busy}>Add Member</button>
              </form>
            </BandToolbar>
          ) : null}
          <BandList>
            {band.members.map((member) => (
              <li key={member.userId}>
                <div className="details">
                  {member.email}
                  <p className="muted">
                    {member.role} / {!member.active ? 'Deactivated' : member.emailVerified ? 'Active' : 'Pending email'}
                  </p>
                </div>
                {band.canManage ? (
                  <button disabled={busy} type="button" onClick={() => {
                    if (window.confirm(
                      `Remove ${member.email} from ${band.name}? This does not deactivate their account.`
                    )) {
                      perform(() => api.removeBandMember(bandId, member.userId), 'Member removed from band.');
                    }
                  }}>
                    Remove From Band
                  </button>
                ) : null}
              </li>
            ))}
          </BandList>
        </>
      ) : null}

      {tab === 'settings' && band.canManage ? (
        <BandToolbar>
          <form onSubmit={(event) => {
            event.preventDefault();
            perform(() => api.renameBand(bandId, name.trim()), 'Band name updated.');
          }}>
            <label>
              Band name
              <input required maxLength={120} value={name} disabled={busy}
                onChange={(event) => setName(event.target.value)} />
            </label>
            <button type="submit" disabled={busy || !name.trim()}>Save Band</button>
          </form>
        </BandToolbar>
      ) : null}
    </BandWorkspace>
  );
}

export default BandPage;
